import React, { FC, useRef, useCallback } from 'react'

import { DisplaySettings, Header, Membership } from '@enuygun/frontend-components'
import { DownloadMobile, EnuygunLogo, Wingie, WingieAr } from '@enuygun/icons'
import { useRouter } from 'next/router'

import { defaultHeaderDesktopLinks, defaultMoreDesktopLinks, GLOBAL_MENU_ITEMS, WINGIE_DESKTOP_LINKS } from './Header.data'
import { getHeaderLinks } from './Header.utils'
import { HeaderWrapper, MobileDisplaySettingsWrapper } from './Header.styled'
import { membershipMarkets, domainURL } from 'utils/domainURL'
import { IHeaderProps } from './HeaderProps'
import { useAppState } from 'context/AppContext'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import { getCookie } from 'utils/cookie'
import { useIsMobile } from 'hooks/useIsMobile'
import { useDeviceType } from 'hooks/useDeviceType'
import { useTranslation } from 'next-i18next'
import { isJsonString, parseJson } from 'utils/json'
import { handleDisplaySettingsChange, type DisplaySettings as DisplaySettingsType, type CurrentState, type DisplaySettingsContext } from 'utils/displaySettingsHelpers'
import { useIsClient } from 'hooks/useIsClient'
import sendTrackerLog from 'utils/tracker'

type AppStateLike = ReturnType<typeof useAppState>

const resolveCurrencyFromCookies = (cookies: ReturnType<typeof getCookie> | null) => {
  if (!cookies?.currency) return null
  return isJsonString(cookies.currency) ? parseJson(cookies.currency) : cookies.currency
}

const resolveLogo = (appState: AppStateLike, isMobile: boolean | null) => {
  if (appState?.market === 'tr') {
    return <EnuygunLogo width={162} height={23.5} style={{ marginTop: isMobile ? 8 : 0 }} />
  }
  if (appState?.language === 'ar') {
    return <WingieAr width={140} height={55} />
  }
  return <Wingie width={140} style={{ marginTop: 10 }} height={22.4} />
}

type DisplaySettingsOnSaveDeps = {
  isDisplaySettingsReady: boolean
  setCurrency?: DisplaySettingsContext['setCurrency']
  currencyConversions?: DisplaySettingsContext['currencyConversions']
  market: string
  language: string
  currencyIso: string
  isMobile: boolean | null
}

const handleDisplaySettingsOnSave = (settings: DisplaySettingsType, deps: DisplaySettingsOnSaveDeps) => {
  // Early return if DisplaySettings is not ready (race condition protection)
  if (!deps.isDisplaySettingsReady) {
    console.warn('[Header] DisplaySettings not ready yet, ignoring onSave call')
    return
  }

  // Additional validation for settings parameter
  if (!settings?.country || !settings?.language || !settings?.currency) {
    console.warn('[Header] Invalid settings received, ignoring onSave call:', settings)
    return
  }

  // Validate required functions are available
  if (typeof deps.setCurrency !== 'function') {
    console.warn('[Header] setCurrency function not available, ignoring onSave call')
    return
  }

  // Prepare current state
  const currentState: CurrentState = {
    market: deps.market,
    language: deps.language,
    currency: deps.currencyIso,
  }

  // Prepare context for handlers
  const context: DisplaySettingsContext = {
    setCurrency: deps.setCurrency,
    currencyConversions: deps.currencyConversions,
  }

  // Use clean, testable handler
  handleDisplaySettingsChange(settings, currentState, context, deps.isMobile ? 'mobile' : 'desktop')
}

const createDisplaySettingsObj = (params: { effectiveMarket: string; language: string; currencyIso: string; onSaveDeps: DisplaySettingsOnSaveDeps }) => ({
  defaultSettings: {
    country: params.effectiveMarket, // Speed Expansion market'i öncelikli kullan
    language: params.language,
    currency: params.currencyIso,
  },
  // onChange: {
  //   country: (country) => console.log('Country changed:', country),
  //   language: (language) => console.log('Language changed:', language),
  //   currency: (currency) => console.log('Currency changed:', currency),
  // },
  onSave: (settings: DisplaySettingsType) => handleDisplaySettingsOnSave(settings, params.onSaveDeps),
})

const getResolvedDisplaySettingsProps = (hasDisplaySettingsData: boolean, isDisplaySettingsReady: boolean, displaySettingsObj: ReturnType<typeof createDisplaySettingsObj>) => {
  if (hasDisplaySettingsData && isDisplaySettingsReady) {
    return displaySettingsObj
  }
  return { ...displaySettingsObj, disabled: true }
}

const resolveHeaderCookies = (cookies: IHeaderProps['cookies']) => {
  if (typeof cookies === 'string') return getCookie(cookies)
  return cookies || null
}

const getHomepageCategory = (isWingie: boolean) => (isWingie ? 'wingie-homepage' : 'enuygun-homepage')

const getDownloadAppTrackingPage = (pathname: string, isWingie: boolean) => {
  if (pathname === '/') return getHomepageCategory(isWingie)
  return pathname
}

const hasValidDisplaySettingsData = (effectiveMarket: string | undefined, language: string | undefined, currencyIso: string | undefined) => {
  if (!effectiveMarket || typeof effectiveMarket !== 'string' || effectiveMarket.length === 0) return false
  if (!language) return false
  return !!currencyIso
}

const isDisplaySettingsInteractionReady = (hasData: boolean, isClient: boolean, setCurrency: unknown, currencyConversions: unknown) => {
  return !!(hasData && isClient && setCurrency && currencyConversions && typeof setCurrency === 'function')
}

const resolveHeaderDisplaySettings = (errorPage: boolean | undefined, showMobileDisplaySettings: boolean, resolvedProps: ReturnType<typeof getResolvedDisplaySettingsProps>) => {
  if (errorPage || showMobileDisplaySettings) return false
  return resolvedProps
}

const resolveDownloadAppHref = (isWingie: boolean, appDownloadLink: string | undefined, routeLink: (market: string) => string, market: string) => {
  if (isWingie && appDownloadLink) return appDownloadLink
  return routeLink(market)
}

const resolveMobileNavigationItems = (isWingie: boolean, wingieMobileLinks: ReturnType<typeof getHeaderLinks>) => {
  if (!isWingie) return undefined
  return { sections: [wingieMobileLinks] }
}

const applyMembershipDefaults = (isWingie: boolean) => {
  if (!Membership) return
  Membership.defaultProps = {
    domainRef: 'enuygun',
    showCorporateSection: !isWingie,
  }
}

const EndesignHeader: FC<IHeaderProps> = (props) => {
  const { t } = useTranslation()
  const router = useRouter()
  const isFavoritesSharePage = router.pathname.includes('favori-paylas')
  const $appState = useAppState()
  const isMobile = useIsMobile()
  const { isTablet } = useDeviceType()
  const isWingie = $appState.market !== 'tr'
  const isClient = useIsClient()
  const headerRef = useRef<HTMLDivElement>(null)
  const $cookies = resolveHeaderCookies(props?.cookies)
  const $currencyFromCookies = resolveCurrencyFromCookies($cookies)
  const homepageCategory = getHomepageCategory(isWingie)

  const routeLink = useCallback(
    (market: string) => {
      const url = domainURL[market]
      const href = $appState?.path_locale ? `${url}/${$appState.path_locale}` : url
      if (market === 'tr') return `${href}/ucak-bileti/mobil-uygulama/indir/`
      return `${href}/mobile-app/download/`
    },
    [$appState?.path_locale]
  )

  const handleDownloadAppClick = useCallback(() => {
    const environment = isMobile || isTablet ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: homepageCategory,
      page: getDownloadAppTrackingPage(router.pathname, isWingie),
      type: 'click',
      message: 'header_download_app_click',
      environment,
      requestId,
      linkUrl: routeLink($appState.market || props.market) || null,
      linkText: t('Header.downloadApp') || null,
      isMobile: !!(isMobile || isTablet),
      market: $appState.market,
    })
  }, [isMobile, isTablet, isWingie, homepageCategory, router.pathname, $appState.market, props.market, t, routeLink])

  const isOpenMembership = membershipMarkets.includes($appState?.market || props.market)
  applyMembershipDefaults(isWingie)

  const modules = [isOpenMembership && Membership /* , (!isWingie || !isMobile) && isClient && CurrencyLocaleChanger */]

  const currentLanguage = $appState?.language || props.language
  const wingieHeaderLinks = $appState?.headerLinks
  const WingieDesktopLinks = getHeaderLinks(
    $appState?.host,
    $appState?.path_locale,
    WINGIE_DESKTOP_LINKS[currentLanguage] || WINGIE_DESKTOP_LINKS.en,
    currentLanguage,
    wingieHeaderLinks
  )
  const WingieMobileLinks = getHeaderLinks($appState?.host, $appState?.path_locale, GLOBAL_MENU_ITEMS[currentLanguage] || GLOBAL_MENU_ITEMS.en, currentLanguage, wingieHeaderLinks)

  const $DesktopItems = isWingie ? WingieDesktopLinks : defaultHeaderDesktopLinks
  const $DesktopMoreItems = isWingie ? [] : defaultMoreDesktopLinks
  const $logo = resolveLogo($appState, isMobile)
  const $logoUrl = $appState?.path_locale ? `/${$appState.path_locale}/` : '/'

  const effectiveMarket = props?.market || $appState.market
  const currencyIso = $appState?.currency?.iso || $currencyFromCookies?.iso || props?.currency?.iso
  const language = $appState.language || props?.language

  const hasDisplaySettingsData = hasValidDisplaySettingsData(effectiveMarket, language, currencyIso)
  const isDisplaySettingsReady = isDisplaySettingsInteractionReady(hasDisplaySettingsData, isClient, $appState.setCurrency, $appState.currencyConversions)

  const displaySettingsObj = createDisplaySettingsObj({
    effectiveMarket,
    language,
    currencyIso,
    onSaveDeps: {
      isDisplaySettingsReady,
      setCurrency: $appState.setCurrency,
      currencyConversions: $appState.currencyConversions,
      market: $appState.market || props?.market || '',
      language: language || '',
      currencyIso: currencyIso || '',
      isMobile,
    },
  })

  const resolvedDisplaySettingsProps = getResolvedDisplaySettingsProps(hasDisplaySettingsData, isDisplaySettingsReady, displaySettingsObj)
  const showMobileDisplaySettings = !!(isMobile || props.ua?.isMobile || isTablet)
  const downloadAppHref = resolveDownloadAppHref(isWingie, wingieHeaderLinks?.appDownloadLink, routeLink, $appState.market || props.market)
  const brandPrefix = $appState.market === 'tr' ? 'enuygun' : 'wingie'
  const showDesktopDownloadApp = !(isMobile || props.ua?.isMobile)

  return (
    <HeaderWrapper ref={headerRef} showBanner={props?.showBanner} isTablet={isTablet} bannerHeight={props?.bannerHeight}>
      <Header
        logo={{
          svg: $logo,
          url: $logoUrl,
        }}
        corporate={!isWingie}
        mobileNavigationItems={resolveMobileNavigationItems(isWingie, WingieMobileLinks)}
        desktopNavigationItems={{
          items: $DesktopItems,
          moreItems: $DesktopMoreItems,
          position: isWingie ? 'flex-end' : 'flex-start',
        }}
        {...props}
        modules={modules}
        hideLoginText
        app={!isWingie}
        currencyChanger={false}
        displaySettings={resolveHeaderDisplaySettings(props?.errorPage, showMobileDisplaySettings, resolvedDisplaySettingsProps)}
        shape={isFavoritesSharePage ? 'default' : 'ghost'}
        data-testid="eds-header"
        showRedirectionBanner={props?.showBanner}
        isTablet={isTablet}
      >
        {showDesktopDownloadApp && (
          <div>
            <a href={downloadAppHref} onClick={handleDownloadAppClick} data-testid={`${brandPrefix}-homepage-header-nav-links-download-app`}>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ fontWeight: '600', fontSize: '14px', fontStyle: 'normal' }}>{t('Header.downloadApp')}</div>
                <div>
                  <DownloadMobile width={24} height={24} />
                </div>
              </div>
            </a>
          </div>
        )}
        {showMobileDisplaySettings && !props?.errorPage && (
          <MobileDisplaySettingsWrapper data-testid="header-mobile-display-settings">
            <DisplaySettings {...resolvedDisplaySettingsProps} showSummary isWingie={false} />
          </MobileDisplaySettingsWrapper>
        )}
      </Header>
    </HeaderWrapper>
  )
}

export default EndesignHeader
